/**
 * FixDesignDataForm — resolve a wrong-DATA PRINT_FILE_ISSUE (`wrong_design`,
 * `color_mismatch`, `low_resolution`, `wrong_position`, `other`).
 *
 * The artwork exists on the print cloud, but the print_file_lookups row points
 * at the WRONG design because the Shopify metafields that composed it were
 * wrong (e.g. a product duplicated from a neighbour with a stale back design).
 * The operator corrects the front/back design data here; the backend recomposes
 * the print filenames, verifies BOTH files exist on the active cloud BEFORE
 * touching the row (never trades a wrong-but-printable row for a 404), upserts
 * the corrected row, writes the corrected Shopify metafields for sibling
 * fan-out (best-effort), and resolves the issue.
 *
 * Prefilled from the row's CURRENT design tokens (getWrongDesignPrefill) so the
 * operator sees what it resolves to today and edits from there.
 *
 * Sides: Print Run suggests which sides the product prints (prefill
 * suggestedSides): the operator-confirmed record when there is one, else the 2
 * rule (a 2 SKU prints front and back, any other SKU the front only).
 * Back-only stays a special case with no switch: the form is the one back
 * field. Otherwise "Prints: Front only / Front and back" lets the operator
 * switch. When an unconfirmed record disagrees with the current print file,
 * nothing is pre-selected and the operator must pick (the current files are
 * shown read-only); a confirmed record whose file drifted pre-selects the
 * stored choice. One amber callout says why the form asks. Every shown side is
 * required and a hidden side is never sent. The choice is saved to this SKU's
 * print record (and its sibling records) in Print Run, which the print station
 * then follows; the Shopify SKU code is never renamed.
 *
 * BLU-1287: the design IDs are PRODUCT-level metafields, so before Save a line
 * says when the choice removes the back from (the server's hasBackToClear),
 * or adds a back to (hasBackToAdd), every variant in Shopify. After a save
 * the server says ADDED a back (`addedBack`, true on a successful retry too),
 * the form hands over to the dialog's variant set-up step (onVariantSetup),
 * which asks the operator to check the back shape and back variant on every
 * variant (they are per-variant) and then advances exactly as before.
 *
 * Mirrors PrintDataForm's layout + submit state machine; used by ResolveDialog.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, AlertTriangle, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { DesignDataFormFooter, type DesignDataSubmitMode } from './DesignDataFormFooter';
import { toast } from 'sonner';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { getUserMessage } from '@/lib/trpc-error-utils';

// Mirrors the backend allowlists (DESIGN_SHAPES + the Light/Dark/Medium variant
// set). "Unset" = leave the field blank — there is NO "None" value.
const SHAPES = ['Rectangular', 'Square', 'Horizontal', 'Circle'] as const;
const VARIANTS = ['Light', 'Dark', 'Medium'] as const;
const UNSET = '__unset__';
const DESIGN_ID_RE = /^[A-Za-z0-9._-]+$/;

type Shape = (typeof SHAPES)[number];
type Variant = (typeof VARIANTS)[number];
type FormStatus = 'idle' | 'saving' | 'error';
type Prefill = RouterOutputs['printAgentAdmin']['getWrongDesignPrefill'];
/** Which sides the product prints (the backend's PrintSides). */
type PrintSides = Prefill['suggestedSides'];

// Literal keys so check:i18n sees every one.
const SIDES_LABEL_KEY = {
  front: 'issues.printFiles.fixDesign.sidesFront',
  front_back: 'issues.printFiles.fixDesign.sidesFrontBack',
  back: 'issues.printFiles.fixDesign.sidesBack',
} as const satisfies Record<PrintSides, string>;
const REQUIRED_KEY = {
  front: 'issues.printFiles.fixDesign.frontRequired',
  front_back: 'issues.printFiles.printData.bothRequired',
  back: 'issues.printFiles.fixDesign.backRequired',
} as const satisfies Record<PrintSides, string>;

/** The current file's sides when they differ from the suggestion, else null. */
function differingSidesOf(data: Prefill): PrintSides | null {
  return data.currentSides !== null && data.currentSides !== data.suggestedSides ? data.currentSides : null;
}

/** The sides to pre-select: none when an unconfirmed record disagrees with the
 *  current file (the operator must pick), else the suggestion. */
function initialSides(data: Prefill): PrintSides | null {
  const mustPick = !data.sidesConfirmed && differingSidesOf(data) !== null && data.suggestedSides !== 'back';
  return mustPick ? null : data.suggestedSides;
}

interface FixDesignDataFormProps {
  issueId: string;
  blockedSku: string | null;
  onResolved: () => void;
  onSavedForUpload: () => void;
  onCancel: () => void;
  /**
   * BLU-1287: a save added a back to a product with other variants. The
   * dialog shows its variant set-up step, whose Continue then does what
   * onResolved ('resolved') or onSavedForUpload ('upload') would have done.
   */
  onVariantSetup: (next: 'resolved' | 'upload', productId: number | null) => void;
}

export function FixDesignDataForm({
  issueId, blockedSku, onResolved, onSavedForUpload, onCancel, onVariantSetup,
}: FixDesignDataFormProps) {
  const { t } = useTranslation('production');
  const utils = trpc.useUtils();

  const prefill = trpc.printAgentAdmin.getWrongDesignPrefill.useQuery(
    { issueId },
    { staleTime: 30_000, retry: 1 },
  );

  const [frontDesignId, setFrontDesignId] = useState('');
  const [backDesignId, setBackDesignId] = useState('');
  const [frontShape, setFrontShape] = useState<Shape | null>(null);
  const [backShape, setBackShape] = useState<Shape | null>(null);
  const [frontVariant, setFrontVariant] = useState<Variant | null>(null);
  const [backVariant, setBackVariant] = useState<Variant | null>(null);
  const [prefilled, setPrefilled] = useState(false);
  // Seeded once from the prefill (null = the operator must pick); then the operator owns it.
  const [sides, setSides] = useState<PrintSides | null>(null);

  const [status, setStatus] = useState<FormStatus>('idle');
  // The button pressed for the save in flight (its spinner); read only while saving.
  const [pressedMode, setPressedMode] = useState<DesignDataSubmitMode>('save_for_upload');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  // Seed the form from the row's current design tokens exactly once — after
  // that the operator's edits own the fields.
  useEffect(() => {
    if (prefilled || !prefill.data) return;
    if (prefill.data.prefillFrontDesignId) setFrontDesignId(prefill.data.prefillFrontDesignId);
    if (prefill.data.prefillBackDesignId) setBackDesignId(prefill.data.prefillBackDesignId);
    setSides(initialSides(prefill.data));
    setPrefilled(true);
  }, [prefill.data, prefilled]);

  const resolveMutation = trpc.printAgentAdmin.resolveWrongDesignData.useMutation();
  const isSaving = status === 'saving';
  // Print Run's suggestion decides the default; the row only says what the
  // file is today (currentSides). Back-only has no switch.
  const data = prefill.data;
  const suggested: PrintSides | null = data?.suggestedSides ?? null;
  // Before the seed effect runs, show the same default it will seed (no flash).
  const chosen: PrintSides | null = prefilled ? sides : data ? initialSides(data) : null;
  const showControl = suggested !== null && suggested !== 'back';
  const showFront = chosen === 'front' || chosen === 'front_back';
  const showBack = chosen === 'front_back' || chosen === 'back';
  const differingSides = data ? differingSidesOf(data) : null;
  // BLU-1287: what the choice changes on the Shopify product (every variant).
  // The server says whether Front only clears a back (the record holds one,
  // or an earlier save's delete is still pending), the same rule as the save.
  const removesBack = chosen === 'front' && data?.hasBackToClear === true;
  // The same for a back added to the product (the record prints the front
  // only, or an earlier save's add is still pending). Back only is offered
  // only over a back-only record, so it never adds one.
  const addsBack = chosen === 'front_back' && data?.hasBackToAdd === true;
  // Why the form asks (amber callout): the stored choice drifted, or the SKU's
  // 2 rule and today's print file disagree.
  const sideLabel = (sides: PrintSides) => t(SIDES_LABEL_KEY[sides]);
  let why: string | null = null;
  if (data && differingSides && suggested) {
    if (data.sidesConfirmed) {
      why = t('issues.printFiles.fixDesign.whyConfirmedDrift', { confirmed: sideLabel(suggested), current: sideLabel(differingSides) });
    } else if (suggested === 'front_back' && differingSides === 'front') {
      why = t('issues.printFiles.fixDesign.whyTwoFrontOnly');
    } else if (suggested === 'front' && differingSides === 'front_back') {
      why = t('issues.printFiles.fixDesign.whyNoTwoFrontBack');
    } else {
      why = t('issues.printFiles.fixDesign.sidesDiffer', { current: sideLabel(differingSides) });
    }
  }
  const mustPick = Boolean(data) && !chosen;
  const showCallout = why !== null || mustPick;
  const consequence = removesBack
    ? data?.currentBackFilename
      ? t('issues.printFiles.fixDesign.consequenceRemovesBack', { file: data.currentBackFilename })
      : t('issues.printFiles.fixDesign.consequenceRemovesBackNoFile')
    : addsBack ? t('issues.printFiles.fixDesign.consequenceAddsBack') : null;
  const describedBy = [showCallout ? 'fd-sides-why' : null, consequence ? 'fd-sides-consequence' : null]
    .filter(Boolean).join(' ') || undefined;

  const front = frontDesignId.trim();
  const back = backDesignId.trim();
  // Only a shown side can block the save: a hidden side is never sent.
  const frontInvalid = showFront && front.length > 0 && !DESIGN_ID_RE.test(front);
  const backInvalid = showBack && back.length > 0 && !DESIGN_ID_RE.test(back);
  // Every side of the chosen sides is required.
  const missingASide = (showFront && front.length === 0) || (showBack && back.length === 0);
  const submitDisabled = isSaving || prefill.isLoading || prefill.isError || !prefill.data || !chosen
    || missingASide || frontInvalid || backInvalid;

  const handleSubmit = async (mode: 'save_for_upload' | 'save_and_resolve' = 'save_for_upload') => {
    if (submitDisabled || !chosen) return;
    setStatus('saving');
    setPressedMode(mode);
    setErrorDetail(null);
    try {
      // Only the chosen sides are sent: Front only sends no back even when the
      // form was seeded from a dual row.
      const res = await resolveMutation.mutateAsync({
        issueId,
        mode,
        sides: chosen,
        frontDesignId: showFront ? front || null : null,
        backDesignId: showBack ? back || null : null,
        frontShape: showFront ? frontShape : null,
        backShape: showBack ? backShape : null,
        frontVariant: showFront ? frontVariant : null,
        backVariant: showBack ? backVariant : null,
      });
      // Marks the cached prefill stale so the next open of this issue reads
      // the new current sides. No network request now, and the open form
      // keeps its seeded state. Every returned status changed the local record.
      void utils.printAgentAdmin.getWrongDesignPrefill.invalidate({ issueId }, { refetchType: 'none' });
      if (res.status === 'metadata_sync_failed') {
        setStatus('error');
        // Shopify confirmed and only the copy to the other variant records
        // failed: say that in the operator's language (no server English).
        setErrorDetail(res.detailCode === 'sibling_sides_pending'
          ? t('issues.printFiles.upload.detail.sibling_sides_pending')
          : t('issues.printFiles.upload.metadataSyncFailed', { message: res.fanOut.error ?? '' }));
        return;
      }
      // The server says this save added a back (a successful retry too):
      // every variant still needs its own back shape and back variant (an
      // unknown variant count still asks).
      const promptVariants = res.addedBack === true && res.fanOut?.siblingVariantCount !== 1;
      const productId = res.fanOut?.productId ?? null;
      if (res.status === 'saved_for_upload' || res.status === 'files_missing') {
        setStatus('idle');
        if (promptVariants) {
          onVariantSetup('upload', productId);
          return;
        }
        onSavedForUpload();
        return;
      }
      const siblings = res.fanOut?.ok ? (res.fanOut.siblingVariantCount ?? 0) : 0;
      toast.success(
        siblings > 1
          ? t('issues.printFiles.fixDesign.resolvedWithSiblings', { count: siblings })
          : t('issues.printFiles.fixDesign.resolved'),
      );
      if (!res.fanOut?.ok && res.fanOut?.error) {
        // Resolve still succeeded; the metafield fan-out is best-effort.
        toast.warning(t('issues.printFiles.printData.fanOutFailed', { message: res.fanOut.error }));
      }
      if (promptVariants) {
        setStatus('idle');
        onVariantSetup('resolved', productId);
        return;
      }
      onResolved();
    } catch (err) {
      setStatus('error');
      setErrorDetail(getUserMessage(err));
    }
  };

  const sideBlocks = [
    {
      side: 'front' as const,
      shown: showFront,
      title: t('issues.printFiles.printData.frontTitle'),
      designId: frontDesignId,
      setDesignId: setFrontDesignId,
      invalid: frontInvalid,
      shape: frontShape,
      setShape: setFrontShape,
      variant: frontVariant,
      setVariant: setFrontVariant,
      current: data?.currentFrontFilename ?? null,
    },
    {
      side: 'back' as const,
      shown: showBack,
      title: t('issues.printFiles.printData.backTitle'),
      designId: backDesignId,
      setDesignId: setBackDesignId,
      invalid: backInvalid,
      shape: backShape,
      setShape: setBackShape,
      variant: backVariant,
      setVariant: setBackVariant,
      current: data?.currentBackFilename ?? null,
    },
  ].filter((s) => s.shown);

  return (
    <div className="space-y-4 py-2">
      {blockedSku && (
        <p className="text-base text-muted-foreground">
          {t('issues.printFiles.printData.skuContext')}{' '}
          <span className="font-mono text-foreground">{blockedSku}</span>
        </p>
      )}

      {prefill.isError && <Alert variant="destructive" role="alert"><AlertDescription className="text-base">{getUserMessage(prefill.error)}</AlertDescription></Alert>}
      {prefill.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-8" role="status" aria-live="polite">
          <Loader2 className="h-5 w-5 motion-safe:animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="text-base text-muted-foreground">{t('issues.printFiles.fixDesign.loading')}</span>
        </div>
      ) : (
        <>
          {/* Prints: Front only / Front and back. Hidden for a back-only
              suggestion, which keeps the one back field. Nothing is checked
              while the operator still has to pick. */}
          {showControl && (
            <div className="space-y-2">
              <Label id="fd-sides-label" className="text-base font-medium">
                {t('issues.printFiles.fixDesign.sidesLabel')}
              </Label>
              <RadioGroup
                aria-labelledby="fd-sides-label"
                aria-required="true"
                aria-describedby={describedBy}
                value={chosen ?? ''}
                onValueChange={(v) => setSides(v as PrintSides)}
                disabled={isSaving}
                className="grid gap-2 sm:grid-cols-2"
              >
                {(['front', 'front_back'] as const).map((option) => {
                  const id = option === 'front' ? 'fd-sides-front' : 'fd-sides-front-back';
                  return (
                    <label
                      key={option}
                      htmlFor={id}
                      className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-md border px-3 text-base transition-colors motion-reduce:transition-none ${
                        chosen === option ? 'border-threadheads-purple bg-threadheads-purple/5' : 'hover:bg-muted/50'
                      }`}
                    >
                      <RadioGroupItem value={option} id={id} />
                      <span>{t(SIDES_LABEL_KEY[option])}</span>
                    </label>
                  );
                })}
              </RadioGroup>
              {/* Must pick: the files the record holds today, read-only. */}
              {mustPick && (data?.currentFrontFilename || data?.currentBackFilename) && (
                <div className="space-y-1 text-base text-muted-foreground">
                  <p>{t('issues.printFiles.fixDesign.currentlyLabel')}</p>
                  <dl className="space-y-1">
                    {([
                      ['front', t('issues.printFiles.printData.frontTitle'), data?.currentFrontFilename],
                      ['back', t('issues.printFiles.printData.backTitle'), data?.currentBackFilename],
                    ] as const).filter(([, , file]) => file).map(([side, title, file]) => (
                      <div key={side} className="flex min-w-0 flex-wrap gap-x-2">
                        <dt>{title}</dt>
                        <dd className="min-w-0 break-all font-mono text-foreground/80">{file}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              {/* One amber callout: why the form asks, and the pick line while
                  nothing is picked. Describes the radio group. */}
              {showCallout && (
                <div
                  id="fd-sides-why"
                  className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-base text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-100"
                >
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                  <div className="space-y-1">
                    {why && <p>{why}</p>}
                    {mustPick && <p className="font-medium">{t('issues.printFiles.fixDesign.sidesPickRequired')}</p>}
                  </div>
                </div>
              )}
              {/* What the chosen option changes on the Shopify product. The
                  polite live region is always rendered, so a change of option
                  is announced; only a long file name wraps anywhere. */}
              <div aria-live="polite" data-fd-consequence-region="">
                {consequence && (
                  <p id="fd-sides-consequence" className="min-w-0 text-base font-medium text-amber-800 [overflow-wrap:anywhere] dark:text-amber-300">
                    {consequence}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Side blocks: the sides the operator chose */}
          {sideBlocks.length > 0 && (
            <div className={`grid gap-4 ${chosen === 'front_back' ? 'sm:grid-cols-2' : ''}`}>
              {sideBlocks.map((s) => (
                <div key={s.side} className="min-w-0 space-y-3 rounded-lg border p-3">
                  <p className="text-base font-semibold uppercase tracking-wide text-muted-foreground">{s.title}</p>
                  {s.current && (
                    <p className="flex items-center gap-1.5 text-base text-muted-foreground">
                      <span className="shrink-0">{t('issues.printFiles.fixDesign.currentlyLabel')}</span>
                      <span className="min-w-0 break-all font-mono text-foreground/80">{s.current}</span>
                      <ArrowRight className="h-3 w-3 shrink-0" aria-hidden="true" />
                    </p>
                  )}
                  <div className="space-y-1.5">
                    <Label htmlFor={`fd-design-${s.side}`} className="text-base font-medium">
                      {t('issues.printFiles.printData.designIdLabel')}
                    </Label>
                    <Input
                      id={`fd-design-${s.side}`}
                      value={s.designId}
                      placeholder={s.side === 'front' ? 'MOTU8' : 'MOTU9'}
                      className="h-12 font-mono"
                      disabled={isSaving}
                      aria-invalid={s.invalid || undefined}
                      onChange={(e) => s.setDesignId(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`fd-shape-${s.side}`} className="text-base font-medium">
                      {t('issues.printFiles.printData.shapeLabel')}
                    </Label>
                    <Select
                      value={s.shape ?? UNSET}
                      onValueChange={(v) => s.setShape(v === UNSET ? null : (v as Shape))}
                      disabled={isSaving}
                    >
                      <SelectTrigger className="h-12" id={`fd-shape-${s.side}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={UNSET}>{t('issues.printFiles.printData.unset')}</SelectItem>
                        {SHAPES.map((sh) => (
                          <SelectItem key={sh} value={sh}>
                            {sh}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`fd-variant-${s.side}`} className="text-base font-medium">
                      {t('issues.printFiles.printData.variantLabel')}
                    </Label>
                    <Select
                      value={s.variant ?? UNSET}
                      onValueChange={(v) => s.setVariant(v === UNSET ? null : (v as Variant))}
                      disabled={isSaving}
                    >
                      <SelectTrigger className="h-12" id={`fd-variant-${s.side}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={UNSET}>{t('issues.printFiles.printData.unset')}</SelectItem>
                        {VARIANTS.map((va) => (
                          <SelectItem key={va} value={va}>
                            {va}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          )}

          {(frontInvalid || backInvalid) && (
            <p className="text-base text-destructive" role="alert">
              {t('issues.printFiles.printData.designIdInvalid')}
            </p>
          )}
          {/* Why the save is disabled, naming only fields that are shown (a
              back-only row has NO front field). The pick line lives in the
              callout above. */}
          {chosen && missingASide && !frontInvalid && !backInvalid && (
            <p className="text-base text-muted-foreground">{t(REQUIRED_KEY[chosen])}</p>
          )}

          {/* The backend rejects (without changing anything) when the corrected
              compose isn't on the print cloud — surface its message verbatim. */}
          {status === 'error' && (
            <Alert variant="destructive" role="alert">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertTitle>{t('issues.printFiles.fixDesign.errorTitle')}</AlertTitle>
              <AlertDescription>
                {errorDetail || t('issues.printFiles.printData.errorGeneric')}
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      <DesignDataFormFooter
        savingMode={isSaving ? pressedMode : null}
        submitDisabled={submitDisabled}
        onCancel={onCancel}
        onSubmit={(mode) => void handleSubmit(mode)}
      />
    </div>
  );
}
